import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import Select from 'react-select'
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MdArrowBack, MdSave, MdDelete, MdGroups2 } from 'react-icons/md';

export default function DiscipleGroupClustersFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let dg_cluster_id = new URLSearchParams(window.location.search).get("dg_cluster_id")

  const [activeTab, setActiveTab] = useState("general")
  const [dgClusterData, setDGClusterData] = useState({
    name: "",
    description: "",
    user_id: "",
  })
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [dgGroupsData, setDgGroupsData] = useState([])

  const tabs = [
    { id: 'general', label: 'General Info' },
    { id: 'groups', label: 'Associated DG Groups' },
  ]

  useEffect(() => {
    fetchPlatformUsers()
    fetchDGClusterData()
    fetchDGGroupByClusterData()
  }, [])

  const fetchDGClusterData = async () => {
    if (!dg_cluster_id || dg_cluster_id === "new") return
    let endpoint = config.endpoint + "/dg/clusters/id/" + dg_cluster_id
    let response = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      setDGClusterData(data.data)
    }
  }

  const fetchDGGroupByClusterData = async () => {
    if (!dg_cluster_id || dg_cluster_id === "new") return
    let endpoint = config.endpoint + "/dg/clusters/groups/" + dg_cluster_id
    let response = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      setDgGroupsData(data.data || [])
    }
  }

  const createDGCluster = async () => {
    let endpoint = config.endpoint + "/dg/clusters/"
    let response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(dgClusterData),
    })
    let data = await response.json()
    if (data.status === 200) {
      notify({ message: "Cluster created successfully", type: "success" })
      navigate("/admin/dg/clusters/details?dg_cluster_id=" + data.data.id)
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const updateDGCluster = async () => {
    let endpoint = config.endpoint + "/dg/clusters/id/" + dg_cluster_id
    let response = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(dgClusterData),
    })
    let data = await response.json()
    if (data.status === 200) {
      notify({ message: "Cluster updated successfully", type: "success" })
      fetchDGClusterData()
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const deleteDGCluster = async () => {
    let endpoint = config.endpoint + "/dg/clusters/id/" + dg_cluster_id
    let response = await fetch(endpoint, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      notify({ message: "Cluster deleted successfully", type: "success" })
      navigate("/admin/dg/clusters")
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const fetchPlatformUsers = async () => {
    setLoading(true)
    let endpoint = config.endpoint + "/users/"
    let response = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      setUsers(data.data || [])
    }
    setLoading(false)
  }

  return (
    <AdminLayout
      navSelected={"DG Clusters"}
      headerText={dg_cluster_id === "new" ? "New DG Cluster" : "Edit DG Cluster"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/dg/clusters')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {dg_cluster_id === "new" ? "Create DG Cluster" : dgClusterData.name || "Edit Cluster"}
            </h1>
          </div>
          <div className="flex gap-2">
            {dg_cluster_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteDGCluster}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (dg_cluster_id === "new") createDGCluster()
              else updateDGCluster()
            }}>
              {dg_cluster_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL INFO */}
      {activeTab === "general" && (
        <Card title="DG Cluster Profile" subtitle="Zone name, appointed cluster leader, and description.">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="input-container">
                <label className='input-label font-semibold'>Cluster Zone Name</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder='e.g. Central-1A'
                  value={dgClusterData.name || ""}
                  onChange={(e) => setDGClusterData({ ...dgClusterData, name: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Cluster Leader</label>
                <Select
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.first_name + " " + user.last_name,
                  }))}
                  value={users.map((user) => ({
                    value: user.id,
                    label: user.first_name + " " + user.last_name,
                  })).find((user) => user.value === dgClusterData.user_id)}
                  onChange={(e) => setDGClusterData({ ...dgClusterData, user_id: e.value })}
                />
              </div>
            </div>

            <div className="input-container">
              <label className='input-label font-semibold'>Zone Description</label>
              <textarea
                className='textarea-input h-32'
                value={dgClusterData.description || ""}
                placeholder="Geographical area or demographics for this cluster zone..."
                onChange={(e) => setDGClusterData({ ...dgClusterData, description: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: ASSOCIATED DG GROUPS */}
      {activeTab === "groups" && (
        <Card title="Disciple Groups in Cluster" subtitle="All active disciple groups assigned to this cluster zone.">
          {dg_cluster_id === "new" ? (
            <p className="text-xs text-gray-400 italic py-6 text-center">Save the cluster profile first to see assigned Disciple Groups.</p>
          ) : dgGroupsData?.length > 0 ? (
            <div className="space-y-3">
              {dgGroupsData.map((dgGroup, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div>
                    <h4 className="font-bold text-sm text-gray-900">{dgGroup.name}</h4>
                    <p className='text-xs text-gray-500 mt-0.5'>{dgGroup?.description || "No description provided."}</p>
                  </div>
                  <Button variant="outline" size="sm" icon={MdGroups2} onClick={() => navigate("/admin/dg/details?dg_id=" + dgGroup.id)}>
                    View Group
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic py-4">No Disciple Groups assigned to this cluster zone.</p>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
